import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { storeMfaPending } from '../lib/mfaChallenge.js';

function routeForRole(role) {
  if (role === 'ATTORNEY') return '/attorney';
  if (role === 'ADMIN') return '/admin';
  return '/client/home';
}

/**
 * Lands here after Google/Apple/Facebook/LinkedIn OAuth completes.
 * Creates a DemandJet session from the verified provider identity.
 */
export default function SocialCallbackPage() {
  const { loginWithProvider } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [message, setMessage] = useState('Finishing sign-in…');

  useEffect(() => {
    const error = params.get('error');
    const provider = params.get('provider') || '';
    if (error) {
      setMessage(error);
      const timer = setTimeout(() => {
        navigate(`/login?social_error=${encodeURIComponent(error)}&provider=${encodeURIComponent(provider)}`, {
          replace: true,
        });
      }, 1600);
      return () => clearTimeout(timer);
    }

    const email = params.get('email') || '';
    const displayName = params.get('displayName') || '';
    const role = (params.get('role') || 'CLIENT').toUpperCase();
    const remember = params.get('remember') !== '0';
    const handoff = params.get('handoff') || '';

    if (!handoff && (!email || !provider)) {
      setMessage('Social sign-in did not return an email address.');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const session = await loginWithProvider({
          provider,
          email,
          displayName,
          role,
          remember,
          handoff,
          mode: 'auto',
        });
        if (cancelled) return;
        if (session?.mfaRequired) {
          storeMfaPending(session);
          navigate('/login?mfa=1', { replace: true });
          return;
        }
        navigate(routeForRole(session.role), { replace: true });
      } catch (err) {
        if (!cancelled) {
          const text = err.message || 'Unable to complete social sign-in';
          setMessage(text);
          navigate(`/login?social_error=${encodeURIComponent(text)}&provider=${encodeURIComponent(provider)}`, {
            replace: true,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, loginWithProvider, navigate]);

  return (
    <div className="boot-screen">
      <div className="boot-mark" aria-hidden />
      <p>{message}</p>
    </div>
  );
}
